import type { AgentsSurface } from '@lib/analytics/agentsExit';
import { splitMediaActions, type SplitMediaActionsOptions } from '@lib/media/mediaActionBar';
import type {
  MediaActionControl,
  MediaMenuAction,
  MediaMenuHostItem,
} from '@lib/media/mediaActions';
import { useFrozenPlayhead } from '@lib/media/useFrozenPlayhead';
import { useMediaVerbs } from '@lib/media/useMediaVerbs';
import { Media } from '@typings/Media';
import { observer } from 'mobx-react';
import { MediaActionBar, type AgentScope, type MediaActionBarLayoutProps } from './MediaActionBar';

interface MediaVerbsActionBarProps
  extends Pick<SplitMediaActionsOptions, 'isTranscriptOnly' | 'canShowCta'>,
    MediaActionBarLayoutProps {
  media: Media;
  /** What a CTA click reports as. Menu rows report themselves — see `MediaActionBar.onAction`. */
  ctaControl: MediaActionControl;
  /** What a click on the unaccented pill beside the CTA reports as. */
  secondaryControl: MediaActionControl;
  /** Which page this is, for `agentsExit`. Not optional here: the whole reason this component
   *  exists is to serve pages that are NOT the library. */
  surface: AgentsSurface;
  /**
   * Overrides what the agent verb does, without changing what it says.
   *
   * The moment modal's search variant uses it to hand over the TRIMMED range
   * (`askAgentAboutMoments`) instead of the whole media. Everything else about the verb — its
   * label, its glyph, when it is inert and why — stays exactly as `useMediaVerbs` produced it,
   * which is the point: it is one capability over a narrower target, not a second verb.
   *
   * An earlier version modelled this as a separate `'agent-on-clip'` id substituted into the
   * action list. That widened the item type across six files to express a difference that was
   * only ever this one function. See the note in `splitMediaActions`.
   *
   * Forwarded straight to the bar, which owns the routing and the contract that comes with it
   * (the handler reports its own click — see `MediaActionBar.onWorkWithAgent`).
   */
  onWorkWithAgent?: (control: MediaActionControl) => void;
  /**
   * Fired just before an action navigates in the SAME tab. A host that renders OVER the
   * destination uses it to get out of the way — see the note at the call site.
   *
   * Only the moment preview on `/search` needs it, because `find-moments` goes to
   * `/search?libs=…&media=…`: same route, so nothing unmounts and the modal would stay open
   * over a grid that had quietly re-scoped to one media.
   */
  onWillNavigate?: () => void;
  /** Reads the host player's position, for the "Copy link at 1:30" row. Frozen on menu open. */
  getCurrentTime?: () => number | undefined;
  /** The range the agent verb is narrowed to, when it is. See `MediaActionBar`. */
  agentScope?: AgentScope;
  /**
   * Rows this host contributes to the ⋯ menu and the mobile drawer, for something the action
   * hooks cannot produce. Forwarded straight to the bar; see `MediaMenuHostItem`.
   */
  hostItems?: MediaMenuHostItem[];
}

/**
 * `MediaActionBar` with only the actions that work on any route.
 *
 * The counterpart to `LibraryMediaActionBar`: same bar, same split, but fed from `useMediaVerbs`
 * alone — so rename, retry and delete are absent rather than dimmed. That's the honest
 * presentation off the library page, where there is no single library in context to perform them
 * against (see the docblock on `useMediaAdminActions`).
 *
 * Safe on `/search` and `/atlas`. Before the hook split, mounting any media menu there
 * white-screened the route.
 */
export const MediaVerbsActionBar = observer(function MediaVerbsActionBar({
  media,
  ctaControl,
  secondaryControl,
  surface,
  onWorkWithAgent,
  onWillNavigate,
  isTranscriptOnly,
  canShowCta,
  getCurrentTime,
  agentScope,
  hostItems,
  ...layout
}: MediaVerbsActionBarProps) {
  const { playheadTime, freezePlayhead } = useFrozenPlayhead(getCurrentTime);

  const { actions, executeVerb } = useMediaVerbs(media, { playheadTime, surface });
  const model = splitMediaActions(actions, { isTranscriptOnly, canShowCta });

  return (
    <MediaActionBar
      {...layout}
      {...model}
      media={media}
      ctaControl={ctaControl}
      secondaryControl={secondaryControl}
      agentScope={agentScope}
      hostItems={hostItems}
      onWorkWithAgent={onWorkWithAgent}
      onMenuOpen={freezePlayhead}
      onAction={(id: MediaMenuAction, control: MediaActionControl) => {
        // `find-moments` is the only verb here that navigates in the SAME tab — the two agent
        // handoffs open a new one. A host rendered over the destination (the moment preview on
        // /search, where this is a query change rather than a route change) would otherwise be
        // left sitting on top of a grid that silently re-scoped underneath it.
        if (id === 'find-moments') onWillNavigate?.();
        void executeVerb(id, control);
      }}
    />
  );
});
